import { UploadCallbacks } from "../CompareBatchNeoPenData/USB/Uploader/USBBatchStrokeUploader";
import { IPageSOBP, isSamePageOnlySOBP, NeoStroke } from '@/neolab-libs/nl-lib3-common';
import { IExamInfo } from '@/repositories/model/transfer/IExamInfo';
import PdfToQAMapper from '@/common/objects/QuestionAnswerToPageMapping/PdfToQAMapper';

interface INeoPenStoreLike {
	requestStrokeRecognition(locale: string | undefined, strokes: NeoStroke[]): Promise<string>;
}

export interface BatchStrokeRecognizerItem {
	ncodeRange: { start: IPageSOBP, end: IPageSOBP };
	wholeStrokes: NeoStroke[];
	userCode: string;
}

export default class BatchStrokeRecognizer {

	private examInfoArray: IExamInfo[];
	private items: BatchStrokeRecognizerItem[];
	private callbacks: UploadCallbacks | undefined;
	private progressKey: string;
	private nextIndex: number = 0;
	private neoPenStore: INeoPenStoreLike;
	private languageCode: string | undefined;
	private testInt: number = 0;

	constructor(
		neoPenStore: INeoPenStoreLike,
		examInfoArray: IExamInfo[],
		items: BatchStrokeRecognizerItem[],
		params?: { key?: string, callbacks?: UploadCallbacks, languageCode?: string }
	) {
		const key = params?.key || 'BatchStrokeRecognizer';
		this.progressKey = `MSRecognizer:${key}:progress`;
		this.callbacks = params?.callbacks;
		this.nextIndex = this.loadProgress();
		this.languageCode = params?.languageCode;

		this.neoPenStore = neoPenStore;
		this.examInfoArray = examInfoArray;
		this.items = items;
	}

	public setCallbacks(callbacks?: UploadCallbacks) {
		this.callbacks = callbacks;
	}

	private loadProgress(): number {
		return this.nextIndex || 0;
	}

	private saveProgress(index: number) {
		this.nextIndex = index;
	}

	public resetProgress() {
		this.nextIndex = 0;
	}
	
	public async recognize(): Promise<{ allSucceeded: boolean; result: Record<string, string>; failedIndices: number[] }> {
		const totalItems = this.items?.length ?? 0;

		const merged: Record<string, string> = {};
		const startIndex = this.nextIndex;

		// Phase 1: classify all remaining items and gather into one map
		const combinedCodeToStrokes: Record<string, NeoStroke[]> = {};
		const codeToItemIndex: Record<string, number> = {};

		for (let i = startIndex; i < totalItems; i++) {
			const item = this.items[i];
			try {
				const grouped = this.classifyStrokes(item.wholeStrokes, item.ncodeRange, this.examInfoArray, item.userCode);
				const keys = Object.keys(grouped);
				for (const k of keys) {
					combinedCodeToStrokes[k] = grouped[k];
					codeToItemIndex[k] = i;
				}
			} finally {
				// no-op
			}
		}

		// Phase 2: process by user chunks (mirrors GAIBatch callback cadence)
		const userCodeToCodes: Record<string, string[]> = {};
		const userCodeToItemIndices: Record<string, Set<number>> = {};
		for (const code of Object.keys(combinedCodeToStrokes)) {
			const userCode = code.split('-')[0];
			if (!userCodeToCodes[userCode]) userCodeToCodes[userCode] = [];
			userCodeToCodes[userCode].push(code);
			const idx = codeToItemIndex[code];
			if (typeof idx === 'number') {
				if (!userCodeToItemIndices[userCode]) userCodeToItemIndices[userCode] = new Set<number>();
				userCodeToItemIndices[userCode].add(idx);
			}
		}
		const userCodes = Object.keys(userCodeToCodes);

		// Keep same chunking semantics as GAIBatch (based on number of questions)
		let numOfQuestions = 0;
		for (let examIndex = 0; examIndex < this.examInfoArray.length; examIndex++) {
			const questions = this.examInfoArray[examIndex].questions;
			numOfQuestions += questions.length;
		}
		let CHUNK_USERS = 1;

		const totalChunks = Math.ceil(userCodes.length / CHUNK_USERS) || 0;
		this.callbacks?.onStart?.(totalChunks);

		let failedIndices: number[] = [];
		let encounteredError = false;
		let chunkIndex = 0;

		for (let start = 0; start < userCodes.length; start += CHUNK_USERS) {
			const sliceUsers = userCodes.slice(start, start + CHUNK_USERS);
			this.callbacks?.onItemStart?.(chunkIndex, undefined as any);

			try {
				// Execute recognition for all codes belonging to these users (in parallel per chunk)
				const codesInChunk: string[] = [];
				for (const u of sliceUsers) {
					for (const code of userCodeToCodes[u]) {
						codesInChunk.push(code);
					}
				}
				const promises = codesInChunk.map(async (code) => {
					const strokes = combinedCodeToStrokes[code] || [];
					if (!strokes.length) {
						return [code, ""] as const;
					}
					const text = await this.neoPenStore.requestStrokeRecognition(this.languageCode, strokes);
					return [code, text ?? ""] as const;
				});
				const results = await Promise.all(promises);
				for (const [code, text] of results) {
					merged[code] = text;
				}

				// Report successes for involved items
				for (const u of sliceUsers) {
					const indices = Array.from(userCodeToItemIndices[u] ?? []);
					for (const idx of indices) {
						this.callbacks?.onItemSuccess?.(idx, undefined as any);
					}
				}
				this.callbacks?.onProgress?.(chunkIndex + 1, totalChunks, []);
				chunkIndex += 1;
			} catch (e) {
				const indices = sliceUsers.flatMap(u => Array.from(userCodeToItemIndices[u] ?? []));
				failedIndices = Array.from(new Set(indices));
				for (const idx of failedIndices) {
					this.callbacks?.onItemError?.(idx, undefined as any, e);
				}
				this.callbacks?.onProgress?.(chunkIndex, totalChunks, failedIndices);
				encounteredError = true;
				break;
			}
		}

		if (!encounteredError && failedIndices.length === 0) {
			this.nextIndex = totalItems;
		} else if (failedIndices.length > 0) {
			this.nextIndex = Math.min(...failedIndices);
		} else {
			this.nextIndex = startIndex;
		}
		this.saveProgress(this.nextIndex);

		const finalProgress = (!encounteredError && failedIndices.length === 0) ? totalChunks : Math.min(chunkIndex, totalChunks);
		this.callbacks?.onProgress?.(finalProgress, totalChunks, failedIndices);

		if (failedIndices.length === 0) {
			this.callbacks?.onComplete?.({ failedIndices: [] });
		} else {
			this.callbacks?.onComplete?.({ failedIndices: [...failedIndices] });
		}

		const allSucceeded = failedIndices.length === 0 && !encounteredError;
		return { allSucceeded, result: merged, failedIndices };
	}

	public async resume(): Promise<{ allSucceeded: boolean; result: Record<string, string>; failedIndices: number[] }> {
		this.nextIndex = this.loadProgress();
		return this.recognize();
	}

	private classifyStrokes(wholeStrokes: NeoStroke[], ncodeRange: { start: IPageSOBP, end: IPageSOBP }, examInfoArray: IExamInfo[], userCode: string) {

		const codeToStrokes: Record<string, NeoStroke[]> = {};

		const sobp: IPageSOBP = {
			section: ncodeRange.start.section,
			owner: ncodeRange.start.owner,
			book: ncodeRange.start.book,
			page: ncodeRange.start.page
		};

		const questionToPageIndexMapping = PdfToQAMapper.readQuestionNameToPageIndex(examInfoArray);
		
		for (let examIndex = 0; examIndex < examInfoArray.length; examIndex++) {
			const questions = examInfoArray[examIndex].questions;

			for (let questionIndex = 0; questionIndex < questions.length; questionIndex++) {
				let questionText = (examIndex + 1) + "-" + (questionIndex + 1);
				let code = userCode + "-" + examInfoArray[examIndex].code + "-" + (questionIndex + 1);

				const { page } = questionToPageIndexMapping[questionText];

				const contentSobp = { ...sobp, page: sobp.page + page };

				const strokes = wholeStrokes.filter((stroke) => isSamePageOnlySOBP(stroke.sobp, contentSobp));
				
				codeToStrokes[code] = [];
				
				for (const stroke of strokes) {
					if (stroke.dotArray[0]?.y > 24) {
						codeToStrokes[code].push(stroke);
					}
				}
			}
		}

		return codeToStrokes;
	}
}


